import { Box, Button, CircularProgress, Tooltip, Typography, styled } from '@mui/material'
import React, { useState } from 'react'
import { authState } from '../state/auth-state'
import { CoverBox, VisuallyHiddenInput } from '../elements/basic-elements'
import CameraIcon from '../images/icons/camera-icon'

import { isTouchDevice } from '../utilities/browser'
import { getErrorMessage } from '../utilities/data'
import { validateAvatar } from '../utilities/avatar'
import PremiumIcon from '../images/icons/premium-icon'
import { colors } from '../constants/colors'
import { PremiumSquare } from './premium-square'

// TODO: Deleting avatar functionality

const PREMIUM_ICON_SIZE_IN_PX = 40

const UserAvatar = ({
  editable,
  premium,
  size,
  onChange
}: {
  editable?: boolean
  premium?: boolean
  size?: number
  onChange?: (string) => void
}) => {
  const { account } = authState()
  const [editButtonVisible, showEditButton] = useState(false)
  const [loading, setLoading] = useState(false)

  const [uploadedImage, setUploadedImage] = useState<string>()

  const avatar = uploadedImage || account?.user?.profile.public.avatar

  const handleFileSelect = async (e: any) => {
    setLoading(true)
    const fReader = new FileReader()
    const file = e.target?.files[0]
    if (!file) {
      setLoading(false)
      return
    }
    fReader.readAsDataURL(e.target?.files[0])
    fReader.onloadend = async event => {
      const result = event?.target?.result as string
      if (result) {
        try {
          const { valid, reason } = await validateAvatar(result)
          if (!valid) {
            alert(reason)
          } else {
            setUploadedImage(result)

            if (onChange) {
              onChange(result)
            }
          }
          setLoading(false)
        } catch (err) {
          setLoading(false)
          alert(getErrorMessage(err, 'Please select a different image.'))
        }
      }
    }
  }

  return (
    <Box
      position="relative"
      onMouseEnter={() => showEditButton(true)}
      onMouseLeave={() => showEditButton(false)}
    >
      {avatar ? <Avatar src={avatar} size={size} /> : <PremiumSquare size={size} />}
      {premium && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            top: -7,
            right: -12.5,
            backgroundColor: colors.RED_100,
            borderRadius: '100px',
            width: PREMIUM_ICON_SIZE_IN_PX,
            height: PREMIUM_ICON_SIZE_IN_PX
          }}
        >
          <PremiumIcon color={colors.WHITE} />
        </Box>
      )}
      {editable && (
        <Box position="absolute" left="50%" bottom={8} sx={{ transform: 'translateX(-50%)' }}>
          <Tooltip
            enterTouchDelay={0}
            placement="top"
            title={
              <Typography variant="body2" color="textPrimary" textAlign="center">
                {'Upload a jpg on png image <5mb'}
              </Typography>
            }
            PopperProps={{
              disablePortal: true,
              style: { width: 300 }
            }}
          >
            <Button
              component="label"
              role={undefined}
              color="primary"
              variant="contained"
              disabled={loading}
              sx={{
                p: 1,
                opacity: !loading && (isTouchDevice() || editButtonVisible) ? 1 : 0
              }}
            >
              <CameraIcon />

              <VisuallyHiddenInput type="file" name="avatar" onChange={handleFileSelect} />
            </Button>
          </Tooltip>
        </Box>
      )}
      {loading && (
        <LoadingBox size={size}>
          <CircularProgress color="info" />
        </LoadingBox>
      )}
    </Box>
  )
}

export const Avatar = styled('img')<{ size?: number }>`
  ${({ size = 64 }) => `
    height: auto;
    display: block;
    max-width: ${size}px;
    min-width: ${size}px;
    object-fit: cover;
    margin-bottom: 0;
    border-radius: ${Math.min(20, size / 4)}px;
    overflow: hidden;
    aspect-ratio: 1;
  `}
`

export const LoadingBox = styled(CoverBox)<{ size?: number }>`
  ${({ size = 64 }) => `
    background: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${Math.min(20, size / 4)}px;
  `}
`

export default UserAvatar
